import type { ApiResponse, PaginatedResponse } from '@klaro7/shared-types';

import api from '@/lib/api';

export interface Lead {
  _id: string;
  name: string;
  email: string;
  type: 'USER' | 'PROFESSIONAL';
  welcomeMailSent: boolean;
  welcomeMailSentAt: string | null;
  welcomeMailStatus: 'sent' | 'failed' | null;
  welcomeMailLastAttemptAt: string | null;
  welcomeMailLastError: string | null;
  createdAt: string;
  updatedAt: string;
  questionnaireId: string | null;
  questionnaireToken: string | null;
  questionnaireUrl: string | null;
  questionnaireStatus: string | null;
}

export interface LeadStats {
  total: number;
  users: number;
  professionals: number;
}

export interface LeadQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  type?: 'USER' | 'PROFESSIONAL';
  search?: string;
}

export async function getLeads(params?: LeadQueryParams): Promise<PaginatedResponse<Lead>> {
  const response = await api.get<PaginatedResponse<Lead>>('/leads', { params });
  return response.data;
}

export async function getLeadStats(): Promise<LeadStats> {
  const response = await api.get<ApiResponse<LeadStats>>('/leads/stats');
  return response.data.data;
}

export interface GenerateQuestionnaireResult {
  questionnaireToken: string;
  questionnaireUrl: string;
}

export async function generateQuestionnaire(leadId: string): Promise<GenerateQuestionnaireResult> {
  const response = await api.post<ApiResponse<GenerateQuestionnaireResult>>(`/leads/${leadId}/generate-questionnaire`);
  return response.data.data;
}

export interface SendWelcomeMailResult {
  sent: boolean;
  error?: string;
}

export async function sendWelcomeMail(leadId: string): Promise<SendWelcomeMailResult> {
  const response = await api.post<ApiResponse<SendWelcomeMailResult>>(`/leads/${leadId}/send-welcome-mail`);
  return response.data.data;
}

/**
 * Public consultant candidacy. Creates a PROFESSIONAL lead (User WAITING_QUESTIONNAIRE
 * + Professional PENDING + questionnaire session) and emails the questionnaire link.
 * This is the correct candidacy path — NOT the generic user signup at /register.
 */
export async function registerProfessionalLead(data: { name: string; email: string }): Promise<void> {
  await api.post('/leads/register', { ...data, type: 'PROFESSIONAL' });
}
